// Products tab — product performance, health scores, dead stock.
import { useEffect, useState } from "react";
import { getProductsAnalytics } from "@/lib/analytics";
import { fmtInt, fmtMoney, fmtPct } from "../components/KpiCard";
import { Card } from "./OverviewTab";
import { Trophy, Skull, AlertTriangle, Sparkles } from "lucide-react";

const TABS = [
  { id: "top_selling", label: "Top vândute", icon: Trophy, key: "units" },
  { id: "top_profit", label: "Top profitabile", icon: Sparkles, key: "profit" },
  { id: "top_returns", label: "Multe retururi", icon: AlertTriangle, key: "returns" },
  { id: "dead_stock", label: "Produse moarte", icon: Skull, key: "stock" },
  { id: "health_top", label: "Health scor — top", icon: Sparkles, key: "score" },
  { id: "health_low", label: "Performanță scăzută", icon: AlertTriangle, key: "score" },
];

export default function ProductsTab({ range }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("top_selling");
  useEffect(() => { getProductsAnalytics(range, 30).then(setData); }, [JSON.stringify(range)]); // eslint-disable-line
  if (!data) return <div className="h-96 rounded-xl bg-card/40 border border-border animate-pulse" />;

  const rows = data[active] || [];

  return (
    <div className="space-y-4" data-testid="analytics-products">
      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`products-tab-${t.id}`}
              onClick={() => setActive(t.id)}
              className={`text-[12px] px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors ${
                active === t.id ? "bg-primary text-primary-foreground" : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
              }`}
            >
              <Icon className="h-3 w-3" />
              {t.label}
            </button>
          );
        })}
      </div>

      <Card title={TABS.find((t) => t.id === active).label}>
        <div className="overflow-x-auto -mx-4">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left py-2 px-4">Produs</th>
                {active === "health_top" || active === "health_low" ? (
                  <>
                    <th className="text-right">Scor</th>
                    <th className="text-right">Unități</th>
                    <th className="text-right">Profit</th>
                    <th className="text-right">Marjă</th>
                    <th className="text-right">Retur %</th>
                  </>
                ) : active === "dead_stock" ? (
                  <>
                    <th className="text-right px-4">Stoc rămas</th>
                  </>
                ) : (
                  <>
                    <th className="text-right">Unități</th>
                    <th className="text-right">Comenzi</th>
                    <th className="text-right">Vânzări</th>
                    <th className="text-right">COGS</th>
                    <th className="text-right">Profit</th>
                    <th className="text-right">Marjă %</th>
                    <th className="text-right">Retururi</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="8" className="text-center text-muted-foreground py-10 text-xs">Fără date</td></tr>
              ) : rows.map((r) => (
                <tr key={r.product_id || r.variant_id} className="border-b border-border/50 hover:bg-secondary/20">
                  <td className="py-2 px-4">
                    <div className="flex items-center gap-3 max-w-md">
                      {r.image ? <img src={r.image} alt="" className="h-9 w-9 rounded-md object-cover bg-secondary/40" /> : <div className="h-9 w-9 rounded-md bg-secondary/40" />}
                      <div className="flex-1 min-w-0 truncate" title={r.title}>{r.title}</div>
                    </div>
                  </td>
                  {(active === "health_top" || active === "health_low") ? (
                    <>
                      <td className="text-right">
                        <ScoreBadge score={r.score} />
                      </td>
                      <td className="text-right tabular-nums">{fmtInt(r.units)}</td>
                      <td className="text-right tabular-nums">{fmtMoney(r.profit)}</td>
                      <td className="text-right tabular-nums">{fmtPct(r.margin_pct)}</td>
                      <td className="text-right tabular-nums">{fmtPct(r.return_rate)}</td>
                    </>
                  ) : active === "dead_stock" ? (
                    <td className="text-right tabular-nums pr-4">{fmtInt(r.stock)}</td>
                  ) : (
                    <>
                      <td className="text-right tabular-nums">{fmtInt(r.units)}</td>
                      <td className="text-right tabular-nums">{fmtInt(r.orders)}</td>
                      <td className="text-right tabular-nums">{fmtMoney(r.revenue)}</td>
                      <td className="text-right tabular-nums text-muted-foreground">{fmtMoney(r.cogs)}</td>
                      <td className="text-right tabular-nums font-semibold">{fmtMoney(r.profit)}</td>
                      <td className="text-right tabular-nums">{fmtPct(r.margin_pct)}</td>
                      <td className="text-right tabular-nums">{fmtInt(r.returns)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 70 ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30"
    : score >= 40 ? "bg-amber-500/10 text-amber-500 border-amber-500/30"
    : "bg-rose-500/10 text-rose-500 border-rose-500/30";
  return <span className={`inline-flex items-center justify-center text-xs px-2 py-0.5 rounded-md border tabular-nums w-12 ${color}`}>{score}</span>;
}
